"use client";

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { ModalShell, type ModalAlign } from "./ModalShell";
import {
  ModalBodyContent,
  ModalFooter,
  ModalHeader,
} from "./ModalParts";
import { useScrollAreaOverflow } from "./useScrollAreaOverflow";

export type ModalSize =
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | "full";

export type ModalPlacement = ModalAlign;

export type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** เนื้อหา body — ส่วนเดียวที่ส่งตอนเรียกใช้ */
  children: ReactNode;
  title?: ReactNode;
  /** แทน header มาตรฐานทั้งก้อน */
  header?: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  /**
   * center = การ์ดกลางจอ, bottom = sheet จากล่าง,
   * right = drawer จากขวา, responsive-drawer = sheet <900px / drawer ≥900px
   */
  placement?: ModalPlacement;
  bodyClassName?: string;
  headerClassName?: string;
  footerClassName?: string;
  panelClassName?: string;
  hideHeader?: boolean;
  hideCloseButton?: boolean;
  closeDisabled?: boolean;
  preventBackdropClose?: boolean;
  zIndexClass?: string;
  overlayClassName?: string;
  /** ไม่ใช้ portal — แค่ header/body/footer (ฝังใน layout อื่น) */
  embedded?: boolean;
  /** เปิดให้ bottom sheet ฝังภายใน panel ของ modal นี้บน desktop */
  sheetHost?: boolean;
  /** โหลดเฉพาะใน body — header/footer แสดงทันที */
  bodyLoading?: boolean;
  bodyLoadingContent?: ReactNode;
  /** inline style ของพื้นที่เนื้อหาใน body */
  bodyStyle?: CSSProperties;
  /** เรียกหลัง exit animation จบ */
  onExited?: () => void;
};

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: "max-w-full md:max-w-sm",
  md: "max-w-full md:max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  full: "max-w-full m-4",
};

function drawerDesktopMaxWidthClass(size: ModalSize): string {
  // 2xl+ matches เตรียมส่งบัญชี (36rem); smaller drawers stay 30rem.
  if (size === "xl" || size === "2xl" || size === "3xl" || size === "4xl") {
    return "min-[900px]:max-w-[36rem]";
  }
  return "min-[900px]:max-w-[30rem]";
}

function panelSurfaceClass(
  placement: ModalPlacement,
  size: ModalSize,
  sheetHost: boolean,
): string {
  const overflow = sheetHost ? "relative overflow-visible" : "overflow-hidden";
  if (placement === "responsive-drawer") {
    return [
      "flex h-[92dvh] max-h-[92dvh] w-full max-w-none min-h-0 flex-col rounded-t-3xl rounded-b-none bg-aera-offwhite shadow-xl",
      `min-[900px]:h-dvh min-[900px]:max-h-dvh ${drawerDesktopMaxWidthClass(size)} min-[900px]:rounded-none min-[900px]:rounded-l-3xl`,
      overflow,
    ].join(" ");
  }
  if (placement === "right") {
    const maxW =
      size === "xl" || size === "2xl" || size === "3xl" || size === "4xl"
        ? "max-w-[36rem]"
        : "max-w-[30rem]";
    return [
      `flex h-dvh max-h-dvh ${maxW} min-h-0 flex-col rounded-none rounded-l-3xl bg-aera-offwhite shadow-xl`,
      overflow,
    ].join(" ");
  }
  if (placement === "bottom") {
    return [
      "flex w-full max-w-none max-h-[92dvh] flex-col rounded-t-3xl rounded-b-none bg-aera-offwhite shadow-xl",
      overflow,
    ].join(" ");
  }
  return [
    "flex h-dvh md:h-auto max-h-dvh md:max-h-[90dvh] flex-col rounded-none md:rounded-4xl bg-aera-offwhite shadow-xl",
    overflow,
    SIZE_CLASSES[size],
  ].join(" ");
}

function ModalFrame({
  onClose,
  title,
  header,
  footer,
  children,
  bodyClassName,
  headerClassName,
  footerClassName,
  hideHeader,
  hideCloseButton,
  closeDisabled,
  bodyLoading,
  bodyLoadingContent,
  bodyStyle,
}: Pick<
  ModalProps,
  | "onClose"
  | "title"
  | "header"
  | "footer"
  | "children"
  | "bodyClassName"
  | "headerClassName"
  | "footerClassName"
  | "hideHeader"
  | "hideCloseButton"
  | "closeDisabled"
  | "bodyLoading"
  | "bodyLoadingContent"
  | "bodyStyle"
>) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const footerElevated = useScrollAreaOverflow(bodyRef, Boolean(footer), [
    children,
    bodyLoading,
  ]);

  return (
    <>
      {!hideHeader &&
        (header ?? (
          <ModalHeader
            title={title}
            onClose={onClose}
            hideCloseButton={hideCloseButton}
            closeDisabled={closeDisabled}
            className={headerClassName}
          />
        ))}
      <ModalBodyContent
        ref={bodyRef}
        loading={bodyLoading}
        loadingContent={bodyLoadingContent}
        style={bodyStyle}
        className={bodyClassName}
      >
        {children}
      </ModalBodyContent>
      {footer ? (
        <ModalFooter elevated={footerElevated} className={footerClassName}>
          {footer}
        </ModalFooter>
      ) : null}
    </>
  );
}

export default function Modal({
  isOpen,
  onClose,
  children,
  title,
  header,
  footer,
  size = "md",
  placement = "center",
  bodyClassName,
  headerClassName,
  footerClassName,
  panelClassName = "",
  hideHeader = false,
  hideCloseButton = false,
  closeDisabled = false,
  preventBackdropClose = false,
  zIndexClass = "z-100",
  overlayClassName,
  embedded = false,
  sheetHost = false,
  bodyLoading = false,
  bodyLoadingContent,
  bodyStyle,
  onExited,
}: ModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const frame = (
    <ModalFrame
      onClose={onClose}
      title={title}
      header={header}
      footer={footer}
      bodyClassName={bodyClassName}
      headerClassName={headerClassName}
      footerClassName={footerClassName}
      hideHeader={hideHeader}
      hideCloseButton={hideCloseButton}
      closeDisabled={closeDisabled}
      bodyLoading={bodyLoading}
      bodyLoadingContent={bodyLoadingContent}
      bodyStyle={bodyStyle}
    >
      {children}
    </ModalFrame>
  );

  if (embedded) {
    if (!isOpen) return null;
    return <>{children}</>;
  }

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      onExited={onExited}
      preventBackdropClose={preventBackdropClose}
      zIndexClass={zIndexClass}
      overlayClassName={overlayClassName}
      align={placement}
      hostPanel={sheetHost}
      panelClassName={[
        panelSurfaceClass(placement, size, sheetHost),
        panelClassName,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {sheetHost ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[inherit]">
          {frame}
        </div>
      ) : (
        frame
      )}
    </ModalShell>
  );
}

export {
  ModalBody,
  ModalBodyContent,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  MODAL_HEADER_CLASSNAME,
} from "./ModalParts";
